/**
 * Sandpack dependency detection for React UI component previews.
 *
 * Centralizes dependency resolution used by both Workspace `Preview` and chat
 * `MockupPreviewPanel`. Handles:
 *   1. Direct and subpath imports (e.g., `@heroicons/react/24/outline` -> `@heroicons/react`)
 *   2. Peer / companion dependencies that Sandpack's client bundler does not
 *      auto-install unless explicitly listed (e.g., `recharts` -> `react-is`).
 */

export const DEPENDENCY_MAP = {
    // Styling & class utilities
    'lucide-react': 'latest',
    'clsx': 'latest',
    'tailwind-merge': 'latest',
    'class-variance-authority': 'latest',

    // Charts & Data Visualization
    'recharts': 'latest',
    'react-is': 'latest',
    'prop-types': 'latest',
    'chart.js': 'latest',
    'react-chartjs-2': 'latest',
    'd3': 'latest',

    // Animation & Effects
    'framer-motion': 'latest',
    'motion': 'latest',
    'canvas-confetti': 'latest',
    'react-confetti': 'latest',
    '@react-spring/web': 'latest',
    'react-countup': 'latest',
    '@formkit/auto-animate': 'latest',

    // Icon packs
    '@heroicons/react': 'latest',
    'react-icons': 'latest',
    '@radix-ui/react-icons': 'latest',
    '@tabler/icons-react': 'latest',
    '@phosphor-icons/react': 'latest',

    // Radix UI / shadcn primitives
    '@radix-ui/react-accordion': 'latest',
    '@radix-ui/react-alert-dialog': 'latest',
    '@radix-ui/react-aspect-ratio': 'latest',
    '@radix-ui/react-avatar': 'latest',
    '@radix-ui/react-checkbox': 'latest',
    '@radix-ui/react-collapsible': 'latest',
    '@radix-ui/react-context-menu': 'latest',
    '@radix-ui/react-dialog': 'latest',
    '@radix-ui/react-dropdown-menu': 'latest',
    '@radix-ui/react-hover-card': 'latest',
    '@radix-ui/react-label': 'latest',
    '@radix-ui/react-menubar': 'latest',
    '@radix-ui/react-navigation-menu': 'latest',
    '@radix-ui/react-popover': 'latest',
    '@radix-ui/react-progress': 'latest',
    '@radix-ui/react-radio-group': 'latest',
    '@radix-ui/react-scroll-area': 'latest',
    '@radix-ui/react-select': 'latest',
    '@radix-ui/react-separator': 'latest',
    '@radix-ui/react-slider': 'latest',
    '@radix-ui/react-slot': 'latest',
    '@radix-ui/react-switch': 'latest',
    '@radix-ui/react-tabs': 'latest',
    '@radix-ui/react-toast': 'latest',
    '@radix-ui/react-toggle': 'latest',
    '@radix-ui/react-toggle-group': 'latest',
    '@radix-ui/react-tooltip': 'latest',

    // Rich UI components (shadcn ecosystem)
    'cmdk': 'latest',
    'vaul': 'latest',
    'sonner': 'latest',
    'react-hot-toast': 'latest',
    'input-otp': 'latest',
    'embla-carousel-react': 'latest',
    'react-resizable-panels': 'latest',
    '@headlessui/react': 'latest',
    'react-day-picker': 'latest',
    'react-dropzone': 'latest',
    'react-select': 'latest',
    'react-markdown': 'latest',
    'remark-gfm': 'latest',

    // Drag & Drop
    '@dnd-kit/core': 'latest',
    '@dnd-kit/sortable': 'latest',
    '@dnd-kit/utilities': 'latest',
    '@hello-pangea/dnd': 'latest',

    // Forms, Validation & State
    'react-hook-form': 'latest',
    '@hookform/resolvers': 'latest',
    'zod': 'latest',
    'yup': 'latest',
    'formik': 'latest',
    'zustand': 'latest',
    'jotai': 'latest',
    '@tanstack/react-table': 'latest',
    '@tanstack/react-query': 'latest',

    // Dates, Routing & Utilities
    'date-fns': 'latest',
    'dayjs': 'latest',
    'lodash': 'latest',
    'axios': 'latest',
    'uuid': 'latest',
    'nanoid': 'latest',
    'react-router-dom': 'latest',
};

/**
 * Peer / companion dependencies that must be installed alongside a primary
 * package in Sandpack even when the component file doesn't import them directly.
 */
const COMPANION_DEPENDENCIES = {
    'recharts': {
        'react-is': 'latest',
        'prop-types': 'latest',
    },
    'react-chartjs-2': {
        'chart.js': 'latest',
    },
    '@hookform/resolvers': {
        'react-hook-form': 'latest',
        'zod': 'latest',
    },
    '@dnd-kit/sortable': {
        '@dnd-kit/core': 'latest',
        '@dnd-kit/utilities': 'latest',
    },
    'react-day-picker': {
        'date-fns': 'latest',
    },
};

const BUILTIN_PACKAGES = new Set(['react', 'react-dom']);

/**
 * Normalize an import specifier (e.g. `@heroicons/react/24/outline` or
 * `lodash/debounce`) to its root npm package name (`@heroicons/react`, `lodash`).
 * Returns null for relative paths or path aliases like `@/...` and `~/...`.
 */
function getRootPackageName(specifier) {
    if (!specifier || specifier.startsWith('.') || specifier.startsWith('/') || specifier.startsWith('~/') || specifier.startsWith('@/')) {
        return null;
    }
    if (specifier.startsWith('@')) {
        const parts = specifier.split('/');
        if (parts.length >= 2 && parts[0].length > 1 && parts[1].length > 0) {
            return `${parts[0]}/${parts[1]}`;
        }
        return null;
    }
    return specifier.split('/')[0] || null;
}

const IMPORT_REGEX = /(?:from\s+['"]([^'"]+)['"]|import\s*\(\s*['"]([^'"]+)['"]\s*\)|require\s*\(\s*['"]([^'"]+)['"]\s*\))/g;

export function extractDependencies(code) {
    const deps = {
        'lucide-react': 'latest',
        'clsx': 'latest',
        'tailwind-merge': 'latest',
    };

    if (!code) return deps;

    const addPackage = (pkg) => {
        if (!pkg || BUILTIN_PACKAGES.has(pkg)) return;
        deps[pkg] = DEPENDENCY_MAP[pkg] || 'latest';
        const companions = COMPANION_DEPENDENCIES[pkg];
        if (companions) {
            Object.entries(companions).forEach(([compPkg, version]) => {
                deps[compPkg] = version;
            });
        }
    };

    // 1. Parse all import/require specifiers (handles subpath imports like
    //    `react-icons/fa`, `@heroicons/react/24/outline`, `@hookform/resolvers/zod`)
    let match;
    IMPORT_REGEX.lastIndex = 0;
    while ((match = IMPORT_REGEX.exec(code)) !== null) {
        const specifier = match[1] || match[2] || match[3];
        const rootPkg = getRootPackageName(specifier);
        if (rootPkg && DEPENDENCY_MAP[rootPkg]) {
            addPackage(rootPkg);
        }
    }

    // 2. Fallback check against DEPENDENCY_MAP keys
    Object.keys(DEPENDENCY_MAP).forEach((pkg) => {
        if (
            code.includes(`from '${pkg}'`) ||
            code.includes(`from "${pkg}"`) ||
            code.includes(`from '${pkg}/`) ||
            code.includes(`from "${pkg}/`)
        ) {
            addPackage(pkg);
        }
    });

    return deps;
}
